import { ulid } from 'ulidx'
import { fold, quoteIdent, quoteLiteral, validName } from '@w/db-custom/names'
import { personCheck, personText, textPerson, type PersonMember } from './person'

export type PropertyKind = 'text' | 'number' | 'checkbox' | 'date' | 'dateTime' | 'url' | 'email' | 'phone' | 'person' | 'select' | 'multiSelect' | 'relation'
export type Statement = { sql: string; params: unknown[] }
type EnumOption = { label: string; color?: string }
type PropertyOptions = { target?: string; options?: readonly EnumOption[] }
type EnumPosition = { before: string; after?: never } | { after: string; before?: never }

const palette = new Set(['default', 'gray', 'brown', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'red'])

function checkedName(name: string): string {
  const normalized = name.normalize('NFC')
  if (!validName(normalized)) throw new Error('Invalid identifier')
  return normalized
}

function identifier(name: string): string {
  return quoteIdent(checkedName(name))
}

/** Generated enum identities are safe SQL words. Turso's type grammar accepts
 * bare type declarations; it does not accept a quoted datatype declaration.
 */
function enumIdentifier(name: string): string {
  const checked = checkedName(name)
  return /^[a-z_][a-z0-9_]*$/iu.test(checked) ? checked : quoteIdent(checked)
}

function statement(sql: string, params: unknown[] = []): Statement {
  return { sql, params }
}

export function createDatabase(input: { table: string }): Statement[] {
  return [statement(`CREATE TABLE ${identifier(input.table)} ("id" TEXT PRIMARY KEY) STRICT`)]
}

export function renameDatabase(old: string, next: string): Statement[] {
  return [statement(`ALTER TABLE ${identifier(old)} RENAME TO ${identifier(next)}`)]
}

export function deleteDatabase(table: string): Statement[] {
  return [statement(`DROP TABLE ${identifier(table)}`)]
}

function columnType(kind: PropertyKind, enumType?: string, target?: string): string {
  switch (kind) {
    case 'text': return 'TEXT'
    case 'number': return 'REAL'
    case 'select':
    case 'multiSelect':
      if (!enumType) throw new Error('Enum type required')
      return `${enumIdentifier(enumType)}${kind === 'multiSelect' ? '[]' : ''}`
    case 'relation':
      if (!target) throw new Error('Relation target required')
      return `TEXT REFERENCES ${identifier(target)}(id)`
    case 'checkbox': return 'boolean'
    case 'dateTime': return 'timestamp'
    case 'date': return kind
    case 'url': case 'email': case 'phone': case 'person': return 'TEXT'
  }
  throw new Error('Unknown property kind')
}

function columnDefinition(kind: PropertyKind, column: string, enumType?: string, target?: string): string {
  const type = columnType(kind, enumType, target)
  if (kind === 'person') return `${type} CHECK (${personCheck(column)})`
  return kind === 'url' || kind === 'email' || kind === 'phone'
    ? `${type} CHECK (${domainCheck(kind, column)})` : type
}

function colorSql(color: string): string {
  if (!palette.has(color)) throw new Error(`Unknown enum color: ${color}`)
  return quoteLiteral(color)
}

function createEnum(type: string, options: readonly EnumOption[]): Statement {
  return statement(`CREATE TYPE ${enumIdentifier(type)} AS ENUM (${options.map(option => `${quoteLiteral(option.label)} WITH (color = ${colorSql(option.color ?? 'default')})`).join(', ')})`)
}

export function addProperty(table: string, name: string, kind: PropertyKind, opts: PropertyOptions = {}): Statement[] {
  const statements: Statement[] = []
  const enumType = kind === 'select' || kind === 'multiSelect' ? `select_${ulid().toLowerCase()}` : undefined
  if (enumType) statements.push(createEnum(enumType, opts.options ?? []))
  statements.push(statement(`ALTER TABLE ${identifier(table)} ADD COLUMN ${identifier(name)} ${columnDefinition(kind, identifier(name), enumType, opts.target)}`))
  return statements
}

export function renameProperty(table: string, old: string, next: string): Statement[] {
  return [statement(`ALTER TABLE ${identifier(table)} RENAME COLUMN ${identifier(old)} TO ${identifier(next)}`)]
}

export function dropProperty(table: string, name: string, kind: PropertyKind, enumType?: string): Statement[] {
  const ownsEnum = kind === 'select' || kind === 'multiSelect'
  if (ownsEnum && !enumType) throw new Error('Enum type required')
  const statements = [statement(`ALTER TABLE ${identifier(table)} DROP COLUMN ${identifier(name)}`)]
  // Validated above; TypeScript cannot narrow enumType through ownsEnum.
  if (ownsEnum) statements.push(statement(`DROP TYPE ${enumIdentifier(enumType as string)}`))
  return statements
}

// Engine contract: deterministic array scalars work inside USING, without subqueries.
function arrayJoin(c: string): string { return `array_to_string(${c}, ', ')` }
function arraySplit(c: string): string {
  const parts = `array_remove(array_trim_elements(string_to_array(${c}, ',')), '')`
  return `CASE WHEN array_length(${parts}) = 0 THEN NULL ELSE ${parts} END`
}
function arrayConstruct(c: string): string { return `ARRAY[${c}]` }
function arrayFirst(c: string): string { return `${c}[1]` }

function displayedText(kind: PropertyKind, c: string, members: readonly PersonMember[] = []): string {
  if (kind === 'person') return personText(c, members)
  if (kind === 'relation') return `(SELECT "row_page"."title" FROM "pages" AS "row_page" WHERE "row_page"."id" = ${c} AND "row_page"."deletedAt" IS NULL)`
  if (kind === 'checkbox') return `CASE ${c} WHEN 1 THEN 'Yes' WHEN 0 THEN 'No' END`
  if (kind === 'number') return `CASE WHEN ${c} = CAST(${c} AS INTEGER) THEN CAST(CAST(${c} AS INTEGER) AS TEXT) ELSE CAST(${c} AS TEXT) END`
  if (kind === 'multiSelect') return arrayJoin(c)
  return c
}

function domainCheck(kind: 'url' | 'email' | 'phone', c: string): string {
  switch (kind) {
    case 'url': return `${c} GLOB 'http://?*' OR ${c} GLOB 'https://?*'`
    case 'email': return `${c} GLOB '?*@?*.?*' AND ${c} NOT GLOB '* *'`
    case 'phone': return `${c} GLOB '[+0-9(]*' AND ${c} NOT GLOB '*[^0-9 ()+.-]*'`
  }
  throw new Error('Unknown domain kind')
}

function usingExpr(from: PropertyKind, to: PropertyKind, c: string, target?: string, members: readonly PersonMember[] = []): string {
  if (to === 'relation') {
    if (!target) throw new Error('Relation target required')
    return `(SELECT "relation_target"."id" FROM ${identifier(target)} AS "relation_target" JOIN "pages" AS "row_page" ON "row_page"."id" = "relation_target"."id" WHERE "row_page"."deletedAt" IS NULL AND "row_page"."title" = ${displayedText(from, c, members)} COLLATE BINARY ORDER BY "relation_target"."id" LIMIT 1)`
  }
  if (to === 'text') return displayedText(from, c, members)
  if (from === 'select' && to === 'multiSelect') return `CASE WHEN ${c} IS NULL THEN NULL ELSE ${arrayConstruct(c)} END`
  if (from === 'multiSelect' && to === 'select') return arrayFirst(c)
  const text = displayedText(from, c, members)
  if (to === 'person') return textPerson(text, members)
  switch (to) {
    // Spec row "any other pair | via Text": Checkbox's Yes/No becomes NULL.
    case 'number': return `CASE WHEN json_valid(trim(${text})) THEN CASE WHEN json_type(trim(${text})) IN ('integer','real') AND abs(CAST(trim(${text}) AS REAL)) <= 1.7976931348623157e308 THEN CAST(trim(${text}) AS REAL) END END`
    case 'checkbox': return `CASE WHEN ${text} IS NULL THEN NULL WHEN lower(trim(${text})) IN ('yes','true','1','x') THEN 1 ELSE 0 END`
    case 'date': return `CASE WHEN lower(trim(${text})) NOT IN ('now','subsec','subsecond') AND date(${text}) IS NOT NULL THEN ${text} END`
    case 'dateTime': return `CASE WHEN lower(trim(${text})) NOT IN ('now','subsec','subsecond') AND datetime(${text}) IS NOT NULL THEN ${text} END`
    case 'url': case 'email': case 'phone': return `CASE WHEN ${domainCheck(to, text)} THEN ${text} END`
    case 'select': return `NULLIF(${text}, '')`
    case 'multiSelect': return arraySplit(text)
  }
  throw new Error('Unknown property kind')
}

/** Rows expose `text`, using the same display expression as enum conversion. */
export function distinctTextsQuery(table: string, column: string, from: PropertyKind, members: readonly PersonMember[] = []): Statement {
  const text = displayedText(from, `${identifier(table)}.${identifier(column)}`, members)
  return statement(`SELECT DISTINCT ${text} AS "text" FROM ${identifier(table)} WHERE ${text} IS NOT NULL AND ${text} <> ''`)
}

/** For →Select/→Multi-select, callers pass distinctTextsQuery's rows mapped to `text`.
 * enumType names the old owned enum; all returned statements run in one transaction.
 * Same-kind requests are refused because a no-op is not a type change.
 */
export function changeType(input: { table: string; column: string; from: PropertyKind; to: PropertyKind; distinctTexts?: readonly string[]; enumType?: string; target?: string; fromTarget?: string; members?: readonly PersonMember[] }): Statement[] {
  if (input.from === input.to) throw new Error('Same-kind conversion is not a type change')
  if ((input.from === 'person' || input.to === 'person') && !input.members) throw new Error('Workspace members required for Person conversion')
  if (input.from === 'relation') {
    const column = identifier(input.column), table = identifier(input.table)
    // custom-databases.spec.md:101/108: target title, then via Text. The
    // frozen server codec forbids SELECT in USING; ordinary UPDATE captures
    // the resolved titles as row changes in this same driver transaction.
    return [
      statement(`ALTER TABLE ${table} ALTER COLUMN ${column} TO ${column} TEXT USING ${column}`),
      statement(`UPDATE ${table} SET ${column} = ${displayedText('relation', `${table}.${column}`)}`),
      ...(input.to === 'text' ? [] : changeType({ ...input, from: 'text' })),
    ]
  }
  const ownsEnum = input.from === 'select' || input.from === 'multiSelect'
  if (ownsEnum && !input.enumType) throw new Error('Enum type required')
  const statements: Statement[] = []
  let enumType = input.enumType
  const preservesEnum = (input.from === 'select' && input.to === 'multiSelect') || (input.from === 'multiSelect' && input.to === 'select')
  if ((input.to === 'select' || input.to === 'multiSelect') && !preservesEnum) {
    if (!input.distinctTexts) throw new Error('Distinct displayed texts required for enum conversion')
    enumType = `select_${ulid().toLowerCase()}`
    const labels = input.to === 'multiSelect' ? input.distinctTexts.flatMap(text => text.split(',').map(part => part.replaceAll(/^ +| +$/g, '')).filter(part => part !== '')) : input.distinctTexts.filter(text => text !== '')
    statements.push(createEnum(enumType, [...new Set(labels)].map(label => ({ label }))))
  }
  const column = identifier(input.column)
  statements.push(statement(`ALTER TABLE ${identifier(input.table)} ALTER COLUMN ${column} TO ${column} ${columnDefinition(input.to, column, enumType, input.target)} USING ${usingExpr(input.from, input.to, input.to === 'relation' ? `${identifier(input.table)}.${column}` : column, input.target, input.members)}`))
  // Validated above; TypeScript cannot narrow input.enumType through ownsEnum.
  if (ownsEnum && !preservesEnum) statements.push(statement(`DROP TYPE ${enumIdentifier(input.enumType as string)}`))
  return statements
}

function positionSql(position?: EnumPosition): string {
  if (position) return position.before === undefined ? ` AFTER ${quoteLiteral(position.after)}` : ` BEFORE ${quoteLiteral(position.before)}`
  return ''
}

export function enumAdd(type: string, label: string, color = 'default', position?: EnumPosition): Statement {
  return statement(`ALTER TYPE ${enumIdentifier(type)} ADD VALUE ${quoteLiteral(label)}${positionSql(position)} WITH (color = ${colorSql(color)})`)
}
export function enumRename(type: string, old: string, next: string): Statement {
  return statement(`ALTER TYPE ${enumIdentifier(type)} RENAME VALUE ${quoteLiteral(old)} TO ${quoteLiteral(next)}`)
}
export function enumSetColor(type: string, label: string, color: string): Statement {
  return statement(`ALTER TYPE ${enumIdentifier(type)} ALTER VALUE ${quoteLiteral(label)} SET (color = ${colorSql(color)})`)
}
export function enumMove(type: string, label: string, position: EnumPosition): Statement {
  return statement(`ALTER TYPE ${enumIdentifier(type)} MOVE VALUE ${quoteLiteral(label)}${positionSql(position)}`)
}
export function enumDrop(type: string, label: string): Statement {
  return statement(`ALTER TYPE ${enumIdentifier(type)} DROP VALUE ${quoteLiteral(label)}`)
}

export function insertRow(table: string, id: string, values: Record<string, unknown> = {}): Statement {
  if (Object.keys(values).some(name => fold(checkedName(name)) === 'id')) throw new Error('Row id must be supplied separately')
  const entries = Object.entries(values)
  const cells = entries.map(([, value]) => cellValue(value))
  return statement(`INSERT INTO ${identifier(table)} (${['id', ...entries.map(([name]) => name)].map(name => identifier(name)).join(', ')}) VALUES (${['?', ...cells.map(cell => cell.sql)].join(', ')})`, [id, ...cells.flatMap(cell => cell.params)])
}
export function updateCell(table: string, id: string, column: string, value: unknown): Statement {
  const cell = cellValue(value)
  return statement(`UPDATE ${identifier(table)} SET ${identifier(column)} = ${cell.sql} WHERE "id" = ?`, [...cell.params, id])
}
/** True for a statement updateCell built for this row; nothing else (a deleteRow has the same WHERE). */
export function isCellUpdate(item: { sql: string; params?: readonly unknown[] }, id: string): boolean {
  return /^UPDATE "(?:[^"]|"")+" SET "(?:[^"]|"")+" = (?:\?|ARRAY\[[?, ]*\]) WHERE "id" = \?$/u.test(item.sql) && item.params?.at(-1) === id
}

function cellValue(value: unknown): { sql: string; params: unknown[] } {
  return Array.isArray(value) ? { sql: `ARRAY[${value.map(() => '?').join(', ')}]`, params: value } : { sql: '?', params: [value] }
}
export function deleteRow(table: string, id: string): Statement {
  return statement(`DELETE FROM ${identifier(table)} WHERE "id" = ?`, [id])
}
/** True for a statement deleteRow built for this row; nothing else (a DELETE with another WHERE or row). */
export function isRowDelete(item: { sql: string; params?: readonly unknown[] }, id: string): boolean {
  return /^DELETE FROM "(?:[^"]|"")+" WHERE "id" = \?$/u.test(item.sql) && item.params?.length === 1 && item.params[0] === id
}

export function titleProjectionName(columns: readonly { name: string }[]): string {
  let alias = '__row_title'
  while (columns.some(column => fold(column.name) === fold(alias))) alias += '_'
  return alias
}

export type DatabaseSort = ({ column: string; pageField?: never } | { pageField: 'editedAt'; column?: never }) & { direction: 'asc' | 'desc' }
export type DatabaseFilter = { column: string; optionId?: string }

export function selectRows(input: { table: string; columns: readonly { name: string; kind: PropertyKind; options?: readonly { id: string; label: string }[] }[]; sort?: DatabaseSort; search?: string; filter?: DatabaseFilter }): Statement {
  let sql = `SELECT p.title AS ${titleProjectionName(input.columns)}, t.* FROM ${identifier(input.table)} t JOIN pages p ON p.id = t.id WHERE p.deletedAt IS NULL`
  const params: unknown[] = []
  if (input.filter?.optionId !== undefined) {
    const column = input.columns.find(column => column.name === input.filter?.column)
    const option = column?.options?.find(option => option.id === input.filter?.optionId)
    if (option && (column?.kind === 'select' || column?.kind === 'multiSelect')) {
      // Enum arrays store exact 64-bit option identities; scalar enum comparison
      // resolves labels. Bind the id as text and cast in SQL without JS rounding.
      sql += column.kind === 'multiSelect' ? ` AND array_contains(t.${identifier(column.name)}, CAST(? AS INTEGER))` : ` AND t.${identifier(column.name)} = ?`
      params.push(column.kind === 'multiSelect' ? option.id : option.label)
    }
  }
  if (input.search) {
    const pattern = `%${input.search.replaceAll(/[\\%_]/g, char => `\\${char}`)}%`
    const searchable = input.columns.filter(column => ['text', 'select', 'url', 'email', 'phone'].includes(column.kind))
    params.push(pattern)
    sql += ` AND (p.title LIKE ? ESCAPE '\\'${searchable.map(column => { params.push(pattern); return ` OR t.${identifier(column.name)} LIKE ? ESCAPE '\\'` }).join('')})`
  }
  if (input.sort && input.sort.direction !== 'asc' && input.sort.direction !== 'desc') throw new Error('Invalid sort direction')
  const sortColumn = input.sort?.pageField === 'editedAt' ? 'p.updatedAt' : input.sort?.column === 'title' ? 'p.title' : input.sort ? `t.${identifier(input.sort.column)}` : 't.id'
  sql += ` ORDER BY ${sortColumn} ${input.sort?.direction.toUpperCase() ?? 'ASC'}${sortColumn === 't.id' ? '' : ', t.id'}`
  return statement(sql, params)
}

/** Cell-shape preflight; date/timestamp validation and enum membership belong to the engine. */
export function isValidForKind(kind: PropertyKind, value: unknown): boolean {
  if (value === null) return true
  if (kind === 'number') return typeof value === 'number' && Number.isFinite(value)
  if (kind === 'checkbox') return value === 0 || value === 1
  if (kind === 'multiSelect') return Array.isArray(value) && value.every(label => typeof label === 'string')
  if (typeof value !== 'string') return false
  if (kind === 'date' || kind === 'dateTime') return true
  // SQLite's GLOB stops at the first NUL byte.
  const checked = value.split('\u0000')[0]
  switch (kind) {
    case 'url': return /^(?:http|https):\/\/[\s\S]+$/.test(checked)
    case 'email': return /^[\s\S]+@[\s\S]+\.[\s\S]+$/.test(checked) && !checked.includes(' ')
    case 'phone': return /^[+0-9(][0-9 ()+.-]*$/.test(checked)
    case 'person': return /^[0-9A-Za-z_]{19}$/.test(value)
    case 'text': case 'select': case 'relation': return true
  }
  throw new Error('Unknown property kind')
}
